/*
File: webserver/src/components/ControllerControls.jsx
*/
import React, { useState, useEffect, useCallback } from 'react';
import io from 'socket.io-client';
import './ControllerControls.css';

/**
 * Props:
 *   selectedDevice – the controller device (rpi/nano)
 *   area           – the current area object (needed for area.id)
 *   site           – the current site object (needed for refresh)
 *   refreshSitesAreasDevices – function to refresh devices after adding TV
 */
function ControllerControls({ selectedDevice, area, site, refreshSitesAreasDevices }) {
  const [command, setCommand] = useState('');
  const [response, setResponse] = useState('');

  // states for Add TV workflow
  const [addTVMode, setAddTVMode] = useState(false);
  const [pairProtocol, setPairProtocol] = useState(null); // 'adb' or 'bluetooth'
  const [deviceName, setDeviceName] = useState('');
  const [deviceIP, setDeviceIP] = useState('');
  const [message, setMessage] = useState('');
  const [pendingTvId, setPendingTvId] = useState(null);
  const [controlledDevices, setControlledDevices] = useState([]);
  const [controlledDevicesError, setControlledDevicesError] = useState('');

  const deviceId = selectedDevice.id;

  const isNano = selectedDevice.type === 'controller-nano';
  const isRpi = selectedDevice.type === 'controller-rpi';

  // Existing shell-command handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!command.trim()) return;
    setResponse('Waiting for response...');
    try {
      const resp = await fetch('/api/command', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceId, command }),
      });
      const data = await resp.json();
      if (data.stdout) setResponse(data.stdout);
      else if (data.stderr) setResponse(data.stderr);
      else if (data.error) setResponse(`Error: ${data.error}`);
      else setResponse(JSON.stringify(data));
    } catch (err) {
      setResponse(`Network error: ${err.message}`);
    }
    setCommand('');
  };

  // Initiate Add TV via chosen protocol
  const handleAddTVClick = (protocol) => {
    setAddTVMode(true);
    setPairProtocol(protocol); // 'adb' for RPI, 'bluetooth' for Nano
    setDeviceName('');
    setDeviceIP('');
    setMessage('');
  };

  const cancelAddTv = useCallback(() => {
    setAddTVMode(false);
    setPairProtocol(null);
    setDeviceName('');
    setDeviceIP('');
    setMessage('');
    setPendingTvId(null);
  }, []);

  // Save the new TV and start pairing (ADB or Bluetooth)
  const handleSaveTV = async () => {
    if (!deviceName.trim() || (pairProtocol !== 'bluetooth' && !deviceIP.trim())) {
      setMessage(
        pairProtocol === 'bluetooth'
          ? 'Please enter device name.'
          : 'Please enter device name and IP address.'
      );
      return;
    }
    if (!area) {
      setMessage('Please select an area first.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please log in again.');
      return;
    }

    // Build payload for /api/devices/save-device
    const payload = {
      deviceName,
      deviceType: 'tv-control',
      areaId: area.id,
      controlledBy: deviceId,
    };
    if (pairProtocol !== 'bluetooth') {
      payload.ip = deviceIP.trim();
    }

    try {
      const res = await fetch('/api/devices/save-device', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const result = await res.json();

      if (res.ok && result.success) {
        // Save JWT for new TV and decode ID
        const jwt = result.jwt;
        const tvId = JSON.parse(atob(jwt.split('.')[1])).deviceId;
        setPendingTvId(tvId);

        // Start pairing via controller
        if (pairProtocol === 'adb') {
          const ip = deviceIP.trim();
          await fetch('/controllers/api/command', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ deviceId, command: `adb connect ${ip}:5555` }),
          });
          await fetch('/controllers/api/command', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ deviceId, command: 'adb devices' }),
          });

          if (refreshSitesAreasDevices && site) {
            await refreshSitesAreasDevices(site.id);
          }
          setAddTVMode(false);
          setDeviceName('');
          setDeviceIP('');
          setMessage('');
          alert('Device registered successfully! Complete any pairing steps on the TV.');
        } else if (pairProtocol === 'bluetooth') {
          await fetch('/controllers/api/command', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ deviceId, command: 'startBluetoothPairing' }),
          });
          setMessage('Waiting for pairing result...');
        }
      } else {
        setMessage(result.message || 'Failed to register device.');
      }
    } catch (err) {
      setMessage('Error while saving device: ' + err.message);
    }
  };

  // Listen for pairing-result events from controllers Socket.IO (not the main /socket.io)
  useEffect(() => {
    if (pairProtocol !== 'bluetooth' || !addTVMode || !pendingTvId) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    const options = {
      path: '/controllers/socket.io',
      transports: ['websocket'],
      auth: { userToken: token },
    };
    const socketUrl = process.env.REACT_APP_SOCKET_URL;
    const controllersSocket = socketUrl ? io(socketUrl, options) : io(options);

    const handler = async (data) => {
      if (!data) return;
      if (data.controllerId !== deviceId) return;
      if (data.name !== deviceName) return;

      try {
        await fetch('/api/devices/update-integration', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ deviceId: pendingTvId, bluetoothMac: data.mac }),
        });
        if (refreshSitesAreasDevices && site) {
          await refreshSitesAreasDevices(site.id);
        }
        cancelAddTv();
        alert('Pairing successful!');
      } catch (err) {
        console.error('Failed to update integration data', err);
        setMessage('Pairing succeeded, but failed to save MAC to the server.');
      }
    };

    controllersSocket.on('connect_error', (err) => {
      console.error('Controllers socket connection error:', err);
      setMessage('Cannot listen for pairing result (controllers socket connection error).');
    });
    controllersSocket.on('pairing-result', handler);

    return () => {
      controllersSocket.off('pairing-result', handler);
      controllersSocket.disconnect();
    };
  }, [pairProtocol, addTVMode, pendingTvId, deviceName, deviceId, refreshSitesAreasDevices, site, cancelAddTv]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !deviceId) return;

    let cancelled = false;
    const load = async () => {
      setControlledDevicesError('');
      try {
        const res = await fetch(`/controllers/api/controllers/${deviceId}/controlled-devices`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok || !data.success) {
          setControlledDevices([]);
          setControlledDevicesError(data.message || 'Failed to load controlled TVs.');
          return;
        }
        setControlledDevices(Array.isArray(data.devices) ? data.devices : []);
      } catch (err) {
        if (cancelled) return;
        setControlledDevices([]);
        setControlledDevicesError(err.message || 'Failed to load controlled TVs.');
      }
    };

    load();
    return () => { cancelled = true; };
  }, [deviceId]);

  return (
    <div className="controllerControls">
      <div className="ccHeader">
        <div>
          <div className="ccTitle">Controller</div>
          <div className="ccSubtitle">
            {selectedDevice.name} <span className="ccMeta">(ID: {deviceId})</span>
          </div>
        </div>
        <div className="ccPills">
          {isNano && <span className="ccPill ccPillNano">Nano</span>}
          {isRpi && <span className="ccPill ccPillRpi">RPI</span>}
        </div>
      </div>

      {/* shell command form */}
      <div className="ccSection">
        <div className="ccSectionTitle">Console</div>
        <form className="ccForm" onSubmit={handleSubmit}>
          <textarea
          value={command}
          onChange={(e) => setCommand(e.target.value)}
          placeholder={
            isNano
              ? 'Enter Nano command (e.g., mem, ws info, ws set 3, ws set 4, ble on, ble off, ble pair, reset-auth, factory-reset) or action key'
              : 'Enter full shell command'
          }
          rows={2}
          className="ccTextarea"
        />
        {isNano && (
          <div className="ccHint">
            Commands: mem, ws info, ws set 3/4, ble on/off, ble pair, reset-auth, factory-reset
          </div>
        )}
          <div className="ccActions">
            <button className="ccBtn ccBtnPrimary" type="submit">Send</button>
          </div>
        </form>
        <div className="ccResponse">
          <div className="ccResponseTitle">Response</div>
          <pre className="ccResponseBody">{response}</pre>
        </div>
      </div>

      {/* Add TV section */}
      <div className="ccSection">
        <div className="ccSectionTitle">Add TV</div>
        {!addTVMode && (
          <div className="ccActions">
            {isRpi && (
              <button className="ccBtn ccBtnPrimary" onClick={() => handleAddTVClick('adb')}>Add TV via ADB</button>
            )}
            {isNano && (
              <button className="ccBtn ccBtnPrimary" onClick={() => handleAddTVClick('bluetooth')}>Add TV via Bluetooth Remote</button>
            )}
          </div>
        )}

        {addTVMode && (
          <div className="ccCard">
            <div className="ccHint">
              {pairProtocol === 'bluetooth'
                ? 'Enter TV name, then put the TV into Bluetooth remote pairing mode.'
                : 'Enter name and IP for the TV (must match Google Home).'}
            </div>

            <div className="ccGrid">
              <label className="ccLabel">
                TV Name
                <input
                  className="ccInput"
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                  placeholder="e.g. Lobby TV"
                  required
                />
              </label>

              {pairProtocol !== 'bluetooth' && (
                <label className="ccLabel">
                  TV IP
                  <input
                    className="ccInput"
                    value={deviceIP}
                    onChange={(e) => setDeviceIP(e.target.value)}
                    placeholder="e.g. 192.168.1.50"
                    required
                  />
                </label>
              )}
            </div>

            <div className="ccActions">
              <button className="ccBtn ccBtnPrimary" onClick={handleSaveTV}>
                {pairProtocol === 'adb' ? 'Save TV & Pair ADB' : 'Save TV & Pair Remote'}
              </button>
              <button className="ccBtn ccBtnSecondary" onClick={cancelAddTv}>Cancel</button>
            </div>

            {message && <div className="ccMessage">{message}</div>}
          </div>
        )}
      </div>

      <div className="ccSection">
        <div className="ccSectionTitle">TVs Controlled By This Device</div>
        {controlledDevicesError && <div className="ccMessage">{controlledDevicesError}</div>}
        {!controlledDevicesError && controlledDevices.length === 0 && (
          <div className="ccEmpty">No TVs assigned to this controller yet.</div>
        )}
        {controlledDevices.length > 0 && (
          <div className="ccList">
            {controlledDevices.map((tv) => (
              <div key={tv.id} className="ccListRow">
                <div className="ccListMain">
                  <div className="ccListName">{tv.name || `Device ${tv.id}`}</div>
                  <div className="ccListMeta">ID: {tv.id}{tv.ip ? ` · IP: ${tv.ip}` : ''}{tv.mac ? ` · MAC: ${tv.mac}` : ''}</div>
                </div>
                <div className="ccListRight">
                  <span className={`ccTag ccTag${(tv.method || 'unknown').toUpperCase()}`}>
                    {(tv.method || 'unknown').toUpperCase()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ControllerControls;
